import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addMetadataElementsToCanvas, connectSfStages } from '../../reducers/united-canvas/unitedCanvasReducer';
import { useConfigurationCanvas } from '../../components/pages/configuration-canvas/public/useConfigurationCanvas';
import { getFullMetadataElementId } from '../../components/pages/funnel-map-flow/metadata-elements/metadataElementUtils';
import { FunnelType } from '../../types/enums/FunnelType';
import { selectDefaultCreationCrmOrgId } from '../../reducers/userInfoReducer';

export const useAddFlowMetadataElement = () => {
  const dispatch = useDispatch();
  const { canvasFunnelMap } = useConfigurationCanvas();
  const crmOrgId = useSelector(selectDefaultCreationCrmOrgId);

  const addTreatmentFlowMetadata = useCallback(() => {
    console.log('🔧 Adding Treatment Flow metadata element and connecting to Treatment Review stage');

    if (!canvasFunnelMap?.funnelsData) {
      console.warn('No funnel map data available');
      return;
    }

    if (!crmOrgId) {
      console.warn('No CRM Org ID available');
      return;
    }

    console.log('Using current environment context:', { crmOrgId });

    // Find the "Active Patient Care" funnel
    const activePatientCareFunnel = Object.values(canvasFunnelMap.funnelsData).find(
      (funnel) => funnel.name.startsWith('Active Patient Care')
    );

    if (!activePatientCareFunnel) {
      console.warn('Active Patient Care funnel not found');
      return;
    }

    // Find the "Treatment Review" stage
    const treatmentReviewStage = activePatientCareFunnel.funnelDetails.stages.find(
      (stage) => stage.stageName === 'Treatment Review'
    );

    if (!treatmentReviewStage) {
      console.warn('Treatment Review stage not found. Please add the Treatment Review stage first (quick2).');
      return;
    }

    console.log('Found Treatment Review stage:', treatmentReviewStage);

    // Get the funnel's absolute position on the canvas
    const funnelPosition = canvasFunnelMap.funnels[activePatientCareFunnel.id]?.position;

    if (!funnelPosition) {
      console.warn('Funnel position not found in funnelMap');
      return;
    }

    console.log('Found funnel position:', funnelPosition);

    // Calculate absolute position: funnel position + stage relative position + offset
    // Treatment Review stage absolute position
    const treatmentReviewAbsoluteColumn = funnelPosition.column + (treatmentReviewStage._stageColumnIndex || 0);
    const treatmentReviewAbsoluteRow = funnelPosition.row + (treatmentReviewStage._branchIndex || 0);

    // Metadata position: 3 rows above and 1 column to the right of Treatment Review (absolute)
    const metadataPosition = {
      column: treatmentReviewAbsoluteColumn + 1,
      row: treatmentReviewAbsoluteRow - 3
    };

    console.log('Treatment Review absolute position:', { column: treatmentReviewAbsoluteColumn, row: treatmentReviewAbsoluteRow });
    console.log('Calculated metadata absolute position:', metadataPosition);

    const payload = {
      metadataElements: [
        {
          name: 'Review Alert',
          type: 'Record-Triggered Flow',
          crmOrgId: crmOrgId,
          metadataId: 'salesforce.Flow.instance.Treatment_Flow_Automation',
          position: metadataPosition,
          color: '#DEFBFF',
          icon: 'flow'
        }
      ],
      connections: []
    };

    console.log('Dispatching addMetadataElementsToCanvas with payload:', payload);

    dispatch(addMetadataElementsToCanvas(payload));

    console.log('🔧 Treatment Flow metadata element added successfully!');

    // Now create the connection from Treatment Review stage to the metadata element
    console.log('🔗 Creating connection from Treatment Review stage to Treatment Flow metadata');

    // Construct the full metadata element ID
    const fullMetadataId = getFullMetadataElementId({
      crmOrgId: crmOrgId,
      metadataId: 'salesforce.Flow.instance.Treatment_Flow_Automation'
    });

    console.log('Connecting Treatment Review stage:', treatmentReviewStage._stageId, 'in funnel:', activePatientCareFunnel.id);
    console.log('To metadata element:', fullMetadataId);

    // Create the connection from Treatment Review stage to metadata element
    dispatch(connectSfStages({
      stageA: {
        funnelId: activePatientCareFunnel.id,
        stageId: treatmentReviewStage._stageId,
      },
      stageB: {
        funnelId: fullMetadataId,
        funnelType: FunnelType.METADATA_ELEMENT,
      },
    }));

    console.log('🔗 Connection from Treatment Review stage to Treatment Flow metadata created successfully!');
  }, [dispatch, canvasFunnelMap, crmOrgId]);

  return { addTreatmentFlowMetadata };
};
